import React, { FC, useEffect, useState } from "react"
import { toast } from "react-toastify"
import { DEF_PG_SIZE_SUB } from '../../constants/maximum-page-size'
import { useLocale } from "../../contexts/localeContext"
import { TableContextProvider } from "../../contexts/table/TableContextProvider"
import { TranslationKeys } from "../../contexts/TranslationKeys"
import columns from "../../models/ColumnDefinitions/CardTypeColumns"
import CardTypeNameColumns from "../../models/ColumnDefinitions/CardTypeNameColumn"
import { ListData } from "../../models/ListData"
import { ListQuery } from "../../models/ListQuery"
import { Lookup } from "../../models/Lookup"
import LookupTypes from "../../models/LookupTypes"
import searchColumns from "../../models/search/CardTypeSearch"
import SiteSearch from "../../models/search/SiteSearch"
import { TabSummary } from "../../models/TabSummary"
import { getAllCardTypes, getCardTypes, getCardTypesNamesWithPagination, updateCardType } from "../../services/cardTypeService"
import { getSiteNamesWithPagination } from "../../services/siteService"
import { showError } from "../../services/utilities/ServiceError"
import ActivityFrame from "../activity-frame/activity-frame"
import AssignModalTwo from "../assignWizard/AssignModal"
import { Button } from "../button/Button"
import TableTab from "../tabs/table-tab"
import { appendFilter } from "@/utilities/filters"

interface Props {
    itemId: string,
    tabs: TabSummary[],
    setTabs: (tabs: TabSummary[]) => void
}

const CardTypes: FC<Props> = ({ itemId, tabs, setTabs }) => {
    const { translate } = useLocale();
    const title = translate.byKey(TranslationKeys.assignedTypes);
    const [busy, setBusy] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const lookup: Lookup[] = [
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        }
    ];

    useEffect(() => {
        dataRefresh((query) => getCardTypeFormats(query));
    },[itemId])

    const getAssignedItems = async (setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setOriginalSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setOriginalSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setBusyLoader: React.Dispatch<React.SetStateAction<boolean>>) => {
        try {
            setBusyLoader(true);
            const cardTypes = await getCardTypesNamesWithPagination({ filters: itemId ? appendFilter(itemId, "Card Type", "truncationFormat.id", []) : []});
            const ids = cardTypes.rows.map(cardType => cardType.value.toString());
            const rows = cardTypes.rows.map(row => ({ id: row.value, name: row.label }));

            setSelectedIds(ids);
            setSelectedRows(rows);
            setOriginalSelectedIds(ids);
            setOriginalSelectedRows(rows);
        } catch (error) {
            showError(error, toast);
        } finally {
            setBusyLoader(false);
        }
    }

    const assignCardTypes = async (toBeAdded: string[], toBeRemoved: string[]) => {
        setBusy(true);

        try {
            await Promise.all([
                ...toBeRemoved.map(id => updateCardType({truncationFormatId:'50', cardTypeId: id.toString()})),
                ...toBeAdded.map(id => updateCardType({truncationFormatId: itemId, cardTypeId:id.toString()}))
            ])
            toast.success(translate.byKey(TranslationKeys.cardTypesUpdated));
            const currentBadge = tabs[0]?.badge ?? 0;
            setTabs([{...tabs[0], badge: currentBadge + toBeAdded.length - toBeRemoved.length}])
        } catch (error) {
            showError(error, toast.warn);
        } finally {
            setBusy(false);
        }

    }
    
    // default to a method that returns nothing
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });
    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    // <-- end context

    const getCardTypeFormats = (listParameters?: ListQuery) => {
        return getCardTypes(itemId, listParameters);
    }

    return (
        <>
            <ActivityFrame busy={busy} className='flex flex-col flex-grow bg-white'>
                <TableContextProvider
                    dataMethod={(listParameters) => getCardTypeFormats(listParameters)}
                    tableDescriptor={columns}
                    searchDescriptor={searchColumns}
                    useTable
                    usePagination
                    useSearch
                    useSort
                    manualDataRefresh={manualDataRefresh}
                    useFilter
                    maxPageSize={DEF_PG_SIZE_SUB}
                    lookup={lookup}>
                    <TableTab
                        title={title}
                        selectRows={false}
                        cyName="cardType">
                        <Button variant="submit" shape="rounded" className="py-1 px-5" onClick={() => setShowAddModal(true)}>{translate.byKey(TranslationKeys.manage)}</Button>
                    </TableTab>
                </TableContextProvider>
                <AssignModalTwo
                    isOpen={showAddModal}
                    assigningTitle={translate.byKey(TranslationKeys.cardTypes)}
                    modalTitle={translate.byKey(TranslationKeys.assignCardTypes)}
                    dataMethod={getAllCardTypes}
                    lookup={lookup}
                    onClose={() => { setShowAddModal(false) }}
                    tableColumns={CardTypeNameColumns}
                    searchColumns={searchColumns}
                    getAssignedItems={getAssignedItems}
                    handleSubmit={assignCardTypes} />
            </ActivityFrame>
        </>
    )
};

export default CardTypes;
